import React, { useEffect, useMemo, useCallback } from 'react';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { useCompassCard } from '@scale/compass-ui/store';
import { VellumContext } from '@scale/genai-compass-shared/cards/vellum';
import { CardType } from '@scale/genai-compass-shared/cardTypes';
import {
  MenuLoadingSkeleton,
  VellumMenu,
  ErrorDisplay,
  VellumInstructions,
  EmployeeInformationTable,
} from './components';
import { useVellumProducts } from './communication/apiClient';

const VellumCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);

  // Use React Query hook instead of manual state management
  const { data: products, isLoading: loading, error } = useVellumProducts();

  // Memoize the context object to prevent unnecessary recreations
  const context = useMemo(
    () =>
      (card?.defaultContext as VellumContext) || {
        cardType: CardType.VELLUM,
        productId: undefined,
        optionSelections: {},
        orderDate: '',
        sfOfficeConfirmation: false,
      },
    [card?.defaultContext],
  );

  // Find the selected product
  const selectedProduct = useMemo(() => {
    if (!products || !context.productId) return null;

    for (const category of products.categories) {
      const product = category.products.find(p => p.id === context.productId);
      if (product) return product;
    }
    return null;
  }, [products, context.productId]);

  // Memoize the context update helper
  const updateContext = useCallback(
    (updates: Partial<VellumContext>) => {
      const updatedContext: VellumContext = {
        ...context,
        ...updates,
      };
      updateCardContext(updatedContext);
    },
    [context],
  );

  // Memoize event handlers
  const handleProductChange = useCallback(
    (productId: string) => {
      const productIdNum = parseInt(productId, 10);
      if (!isNaN(productIdNum)) {
        // Reset option selections when product changes
        updateContext({
          productId: productIdNum,
          optionSelections: {},
        });
      }
    },
    [updateContext],
  );

  const handleOptionChange = useCallback(
    (optionName: string, valueId: string) => {
      const updatedSelections = { ...context.optionSelections };

      if (valueId === 'none') {
        // Remove the option if "none" is selected
        delete updatedSelections[optionName];
      } else {
        updatedSelections[optionName] = valueId;
      }

      updateContext({ optionSelections: updatedSelections });
    },
    [context.optionSelections, updateContext],
  );

  const handleConfirmationChange = useCallback(
    (checked: boolean) => {
      updateContext({ sfOfficeConfirmation: checked });
    },
    [updateContext],
  );

  // Update card can submit status
  useEffect(() => {
    const canSubmit = !!context.productId && !!context.sfOfficeConfirmation;
    updateCardCanSubmit(canSubmit);
  }, [context.productId, context.sfOfficeConfirmation]);

  return (
    <div className="flex flex-row gap-8">
      <div className="w-2/3">
        <VellumInstructions />

        <div className="mt-6">
          <EmployeeInformationTable />
        </div>
      </div>

      {loading ? (
        <MenuLoadingSkeleton />
      ) : error ? (
        <ErrorDisplay error={error instanceof Error ? error.message : 'Failed to fetch products'} />
      ) : (
        products && (
          <VellumMenu
            context={context}
            products={products}
            selectedProduct={selectedProduct}
            onProductChange={handleProductChange}
            onOptionChange={handleOptionChange}
            onConfirmationChange={handleConfirmationChange}
          />
        )
      )}
    </div>
  );
};

export default VellumCardComponent;
